/**
 * The ad as typed — «Write the ad» or «Use an existing tweet» — once, for the two screens
 * that make one: the wizard's step 5 (`pages/CreateAd.tsx`) and the card's Add ad
 * (`components/AddAd.tsx`).
 *
 * 🔴 **Moved here from the wizard on 2026-10-21 (M2), byte for byte in meaning.** The
 * words are `lib/create-ad-copy.ts`'s and the decisions `lib/create-ad.ts`'s; what this
 * component holds is only what the step holds while it is open — the tweets read for the
 * pick, the lookup typed, the previews of the files chosen — and the draft itself is the
 * caller's, written through `set`. A second copy of this step on the card would have
 * been a second place for the attach's input to drift from the wizard's.
 *
 * 🔴 The files are held in the draft and uploaded by the run, never on change: a case
 * reads this file for `set({ adFiles: Array.from(e.target.files` with no `api.` beside it.
 */
import { useEffect, useRef, useState } from "react";
import type { AdDraft } from "../lib/ad-run.ts";
import { type LineItemObjective, type Tweet, UnauthorizedError, api } from "../lib/api.ts";
import {
	AD_HELP,
	AD_MODES,
	AD_NAME_HELP,
	AD_NAME_LABEL,
	AD_TEXT_LABEL,
	AD_URL_LABEL,
	CARD_IMAGE_HELP,
	CARD_IMAGE_LABEL,
	CARD_TITLE_HELP,
	CARD_TITLE_LABEL,
	CARD_URL_HELP,
	EXISTING_HELP,
	EXISTING_NO_LINK,
	FIND_TWEET,
	LIST_TWEETS,
	MEDIA_HELP,
	MEDIA_LABEL,
	MEDIA_VIDEO_NOTE,
	NO_TWEETS,
	PICK_TWEET,
	REMOVE_FILE,
	REPLIES_HELP,
	REPLIES_LABEL,
	REPLIES_OPTIONS,
	TIMELINE_LABEL,
	TIMELINE_PLACEHOLDER,
	TWEET_ID_HELP,
	TWEET_ID_LABEL,
	WEBSITE_AS,
	WEBSITE_AS_HELP,
	WRITE_HELP,
	cardRatioText,
	textCounter,
	urlHelp,
} from "../lib/create-ad-copy.ts";
import {
	type AdMode,
	CARD_IMAGE_TYPES,
	type WebsiteAs,
	cardMode,
	cardRatioOff,
	defaultTweetName,
	linkUnseen,
	urlRequired,
} from "../lib/create-ad.ts";
import { TIMELINES, TIMELINE_MEANS, tweetLabel } from "../lib/creative-copy.ts";
import { Button, InfoBanner, Input, Select, Textarea } from "../ui/index.ts";

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

interface Props {
	/** X's account id — the tweets read are this account's. */
	xAccountId: string;
	/** The line item's objective: under WEBSITE_CLICKS the link is required (the URL field's help). */
	objective: LineItemObjective;
	draft: AdDraft;
	set: (patch: Partial<AdDraft>) => void;
}

export function AdStep({ xAccountId, objective, draft, set }: Props) {
	// ── The account's tweets, by page or by id ──────────────────────
	const [timeline, setTimeline] = useState("");
	const [lookup, setLookup] = useState("");
	const [tweets, setTweets] = useState<Tweet[] | null>(null);
	const [tweetsBusy, setTweetsBusy] = useState(false);
	const [tweetsFailed, setTweetsFailed] = useState<string | null>(null);

	async function readTweets(by: "page" | "id") {
		if (!xAccountId || tweetsBusy) return;
		const id = lookup.trim();
		if (by === "page" && !timeline) return;
		if (by === "id" && !/^[0-9]+$/.test(id)) return;
		setTweetsBusy(true);
		setTweetsFailed(null);
		try {
			const page =
				by === "page"
					? await api.tweets(xAccountId, "PUBLISHED", timeline)
					: await api.tweetsById(xAccountId, "PUBLISHED", [id]);
			setTweets(page.tweets);
			// A new list, an empty choice: a tweet picked from the previous list is a row
			// that is no longer on screen (the creative panel's rule, d6, #147).
			set({ tweet: null });
		} catch (e) {
			if (e instanceof UnauthorizedError) return;
			setTweets(null);
			setTweetsFailed(message(e));
		} finally {
			setTweetsBusy(false);
		}
	}

	// ── The card's image: its size read in the browser, for the guide's limits ──
	// Only the latest choice may write its size: an earlier read finishing late would put
	// another file's width beside this one.
	const choice = useRef<File | null>(null);
	function chooseCardImage(files: File[]) {
		// The file is held, not uploaded, as every file here is; only its size is read.
		set({ adFiles: files, cardImage: null });
		const file = files.length === 1 ? files[0] : null;
		choice.current = file;
		if (file === null) return;
		void createImageBitmap(file).then(
			(bitmap) => {
				if (choice.current === file)
					set({ cardImage: { width: bitmap.width, height: bitmap.height } });
				bitmap.close();
			},
			() => {
				// Not an image the browser can read: the size stays unread, and the type check
				// or «unread» says so.
			},
		);
	}
	const asCard = cardMode({ objective, adMode: draft.adMode, adWebsiteAs: draft.adWebsiteAs });

	// ── Previews of the files chosen, revoked when they change ──────
	const [previews, setPreviews] = useState<{ name: string; url: string }[]>([]);
	useEffect(() => {
		const urls = draft.adFiles.map((f) => ({ name: f.name, url: URL.createObjectURL(f) }));
		setPreviews(urls);
		return () => {
			for (const u of urls) URL.revokeObjectURL(u.url);
		};
	}, [draft.adFiles]);

	return (
		<div className="grid gap-4">
			<p className="text-sm text-muted">{AD_HELP}</p>
			<div className="flex flex-wrap gap-2" role="tablist">
				{(Object.keys(AD_MODES) as AdMode[]).map((mode) => (
					<Button
						key={mode}
						type="button"
						role="tab"
						aria-selected={draft.adMode === mode}
						variant={draft.adMode === mode ? "primary" : "outline"}
						size="sm"
						onClick={() => set({ adMode: mode })}
					>
						{AD_MODES[mode]}
					</Button>
				))}
			</div>
			{draft.adMode === "write" && urlRequired(objective) && (
				<div className="grid gap-2">
					<div className="flex flex-wrap gap-2" role="tablist">
						{(Object.keys(WEBSITE_AS) as WebsiteAs[]).map((as) => (
							<Button
								key={as}
								type="button"
								role="tab"
								aria-selected={draft.adWebsiteAs === as}
								variant={draft.adWebsiteAs === as ? "primary" : "outline"}
								size="sm"
								onClick={() => set({ adWebsiteAs: as, adFiles: [], cardImage: null })}
							>
								{WEBSITE_AS[as]}
							</Button>
						))}
					</div>
					<p className="text-xs text-muted">{WEBSITE_AS_HELP}</p>
				</div>
			)}
			{asCard && (
				<div className="grid gap-4">
					<Textarea
						label={AD_TEXT_LABEL}
						value={draft.adText}
						onChange={(e) => set({ adText: e.target.value })}
						helperText={textCounter(draft.adText)}
						rows={4}
					/>
					<Input
						label={AD_NAME_LABEL}
						value={draft.adName}
						onChange={(e) => set({ adName: e.target.value })}
						placeholder={defaultTweetName(draft.adText) || undefined}
						helperText={AD_NAME_HELP}
					/>
					<Input
						label={CARD_TITLE_LABEL}
						value={draft.cardTitle}
						onChange={(e) => set({ cardTitle: e.target.value })}
						helperText={CARD_TITLE_HELP}
					/>
					<Input
						label={AD_URL_LABEL}
						type="url"
						inputMode="url"
						value={draft.adUrl}
						onChange={(e) => set({ adUrl: e.target.value })}
						helperText={CARD_URL_HELP}
					/>
					<div className="grid gap-1.5">
						<label className="text-sm font-medium text-content" htmlFor="card-image">
							{CARD_IMAGE_LABEL}
						</label>
						<input
							id="card-image"
							type="file"
							accept={CARD_IMAGE_TYPES.join(",")}
							onChange={(e) => chooseCardImage(Array.from(e.target.files ?? []))}
						/>
						<p className="text-xs text-muted">{CARD_IMAGE_HELP}</p>
					</div>
					{draft.cardImage !== null && cardRatioOff(draft.cardImage) && (
						<InfoBanner variant="warning" title={cardRatioText(draft.cardImage)} />
					)}
					{previews.length > 0 && (
						<img
							src={previews[0].url}
							alt={previews[0].name}
							className="h-24 w-auto rounded-md object-cover"
						/>
					)}
				</div>
			)}
			{draft.adMode === "write" && !asCard && (
				<div className="grid gap-4">
					<p className="text-sm text-muted">{WRITE_HELP}</p>
					<Textarea
						label={AD_TEXT_LABEL}
						value={draft.adText}
						onChange={(e) => set({ adText: e.target.value })}
						helperText={textCounter(draft.adText)}
						rows={4}
					/>
					<Input
						label={AD_NAME_LABEL}
						value={draft.adName}
						onChange={(e) => set({ adName: e.target.value })}
						placeholder={defaultTweetName(draft.adText) || undefined}
						helperText={AD_NAME_HELP}
					/>
					<Input
						label={AD_URL_LABEL}
						type="url"
						inputMode="url"
						value={draft.adUrl}
						onChange={(e) => set({ adUrl: e.target.value })}
						helperText={urlHelp(objective)}
					/>
					<div className="grid gap-1.5">
						<label className="text-sm font-medium text-content" htmlFor="ad-files">
							{MEDIA_LABEL}
						</label>
						<input
							id="ad-files"
							type="file"
							multiple
							accept="image/jpeg,image/png,image/webp,image/gif"
							onChange={(e) => {
								// The files are held, not uploaded: the run sends them at Create (paused) or Attach.
								set({ adFiles: Array.from(e.target.files ?? []) });
							}}
						/>
						<p className="text-xs text-muted">{MEDIA_HELP}</p>
						<p className="text-xs text-muted">{MEDIA_VIDEO_NOTE}</p>
					</div>
					{previews.length > 0 && (
						<ul className="flex flex-wrap gap-3">
							{previews.map((p, i) => (
								<li key={p.url} className="grid gap-1 text-xs text-muted">
									<img src={p.url} alt={p.name} className="h-24 w-24 rounded-md object-cover" />
									<span className="max-w-24 truncate">{p.name}</span>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										// By position, not by name: two files of one name are two files (the reviewer's 🔵 on #169).
										onClick={() => set({ adFiles: draft.adFiles.filter((_, j) => j !== i) })}
									>
										{REMOVE_FILE}
									</Button>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
			{draft.adMode === "write" && (
				<div className="grid gap-1.5">
					<div className="w-72">
						<Select
							label={REPLIES_LABEL}
							value={draft.adReplies}
							onChange={(e) => set({ adReplies: e.target.value as AdDraft["adReplies"] })}
							options={REPLIES_OPTIONS.map((o) => ({ ...o }))}
						/>
					</div>
					<p className="text-xs text-muted">{REPLIES_HELP}</p>
				</div>
			)}
			{draft.adMode === "existing" && (
				<div className="grid gap-4">
					<p className="text-sm text-muted">{EXISTING_HELP}</p>
					<div className="flex flex-wrap items-end gap-3">
						<div className="w-56">
							<Select
								label={TIMELINE_LABEL}
								value={timeline}
								onChange={(e) => setTimeline(e.target.value)}
								placeholder={TIMELINE_PLACEHOLDER}
								options={TIMELINES.map((t) => ({ value: t, label: t }))}
							/>
						</div>
						<Button
							type="button"
							variant="outline"
							size="sm"
							loading={tweetsBusy}
							disabled={!timeline}
							onClick={() => void readTweets("page")}
						>
							{LIST_TWEETS}
						</Button>
					</div>
					{timeline && (
						<p className="text-xs text-muted">
							{TIMELINE_MEANS[timeline as keyof typeof TIMELINE_MEANS]}
						</p>
					)}
					<div className="flex flex-wrap items-end gap-3">
						<div className="w-72">
							<Input
								label={TWEET_ID_LABEL}
								inputMode="numeric"
								value={lookup}
								onChange={(e) => setLookup(e.target.value)}
								helperText={TWEET_ID_HELP}
							/>
						</div>
						<Button
							type="button"
							variant="outline"
							size="sm"
							loading={tweetsBusy}
							disabled={!/^[0-9]+$/.test(lookup.trim())}
							onClick={() => void readTweets("id")}
						>
							{FIND_TWEET}
						</Button>
					</div>
					{tweetsFailed && (
						<InfoBanner variant="danger" title="X's tweets could not be read.">
							{tweetsFailed}
						</InfoBanner>
					)}
					{tweets !== null && (
						<fieldset className="grid gap-2">
							<legend className="text-sm text-muted">{PICK_TWEET}</legend>
							{tweets.length === 0 && <p className="text-sm text-muted">{NO_TWEETS}</p>}
							{tweets.map((t) => (
								<label key={t.id} className="flex items-start gap-2 text-sm text-content">
									<input
										type="radio"
										name="tweet"
										checked={draft.tweet?.id === t.id}
										onChange={() => set({ tweet: t })}
									/>
									<span>
										{tweetLabel(t)}
										<span className="ml-2 font-mono text-xs text-muted">{t.id}</span>
									</span>
								</label>
							))}
						</fieldset>
					)}
					{linkUnseen(objective, draft.tweet) && (
						<InfoBanner variant="warning" title={EXISTING_NO_LINK.title}>
							{EXISTING_NO_LINK.body}
						</InfoBanner>
					)}
				</div>
			)}
		</div>
	);
}
